import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useBrandManager } from "@/hooks/useBrandManager";

export interface TaskCreationSettings {
  id: string;
  brand_id: string;
  allow_same_day_tasks: boolean;
  allow_off_sprint_dates: boolean;
  due_date_required: boolean;
  require_time_for_completion: boolean;
  allow_manual_time_adjustment: boolean;
  internal_task_visible: boolean;
  estimated_time_mandatory: boolean;
  require_verb_in_title: boolean;
  project_enabled: boolean;
  growth_standup_enabled: boolean;
  growth_standup_slack_enabled: boolean;
  growth_standup_slack_channel: string | null;
  growth_standup_report_brand_id: string | null;
  copy_link_on_submission: boolean;
  copy_link_type: 'technical' | 'friendly';
  sync_with_google_calendar_on_tasks: boolean;
  created_at: string;
  updated_at: string;
}

export const useTaskCreationSettings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentBrandId } = useBrandManager();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['task-creation-settings', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return null;

      const { data, error } = await supabase
        .from('task_creation_settings')
        .select('*')
        .eq('brand_id', currentBrandId)
        .maybeSingle();

      if (error) throw error;

      // Return defaults if no settings exist
      if (!data) {
        return {
          brand_id: currentBrandId,
          allow_same_day_tasks: true,
          allow_off_sprint_dates: false,
          due_date_required: false,
          require_time_for_completion: false,
          allow_manual_time_adjustment: true,
          internal_task_visible: true,
          estimated_time_mandatory: false,
          require_verb_in_title: false,
          project_enabled: true,
          growth_standup_enabled: false,
          growth_standup_slack_enabled: false,
          growth_standup_slack_channel: null,
          growth_standup_report_brand_id: null,
          copy_link_on_submission: true,
          copy_link_type: 'friendly',
          sync_with_google_calendar_on_tasks: false,
        };
      }

      return data;
    },
    enabled: !!currentBrandId,
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: Partial<TaskCreationSettings>) => {
      if (!currentBrandId) throw new Error('No brand selected');

      // Check if settings exist
      const { data: existing } = await supabase
        .from('task_creation_settings')
        .select('id')
        .eq('brand_id', currentBrandId)
        .maybeSingle();

      if (existing) {
        // Update existing settings
        const { data, error } = await supabase
          .from('task_creation_settings')
          .update(updates)
          .eq('brand_id', currentBrandId)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new settings
        const { data, error } = await supabase
          .from('task_creation_settings')
          .insert({
            brand_id: currentBrandId,
            ...updates,
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-creation-settings', currentBrandId] });
      toast({
        title: "Success",
        description: "Task creation rules updated successfully",
      });
    },
    onError: (error: any) => {
      const isRLSError = error?.message?.includes('row-level security') || 
                         error?.code === '42501' || 
                         error?.message?.includes('policy');
      
      toast({
        title: "Error",
        description: isRLSError 
          ? "You don't have permission to update settings for this brand. Please ensure you're a member of this brand's Growth Squad."
          : "Failed to update task creation rules. Please try again.",
        variant: "destructive",
      });
      console.error('Error updating task creation settings:', error);
    },
  });

  return {
    settings,
    isLoading,
    updateSettings,
  };
};
